<template>
  <div
    class="upload-container"
    :class="{
      'upload-container--hover': hover,
      'upload-container--has-file': currentAttachments.length > 0,
      'input-dirty': inputDirty
    }"
    v-validate="errors"
    @drop.prevent="imgChanged"
    @dragover.prevent=""
    @dragenter.prevent="dragEnter"
    @dragleave.prevent="dragLeave"
  >
    <!-- Attachments list -->
    <ul v-if="currentAttachments.length" class="upload-container__file-group">
      <li class="upload-container__file" v-for="(attachment,index) in currentAttachments" :key="index"
          :class="{'upload-container__file--valid': !attachment.formatError && !attachment.maxSizeError && !attachment.resolutionError,
                     'upload-container__file--invalid': attachment.formatError || attachment.maxSizeError || attachment.resolutionError,}">
        <!-- Attachment name and errors -->
        <div class="f-center">
          <em v-if="!attachment.formatError && !attachment.maxSizeError && !attachment.resolutionError"
              class="icon icon--attachment-green mr-1-768 mr-3"></em>
          <em v-else
              class="icon icon--attachment-orange mr-1-768 mr-3"></em>
          <h4 class="font-small">
            <span class="file-name">{{ attachment.fileName }}</span>
            <span class="font-orange font-light font-tiny upload-container__file__error"
                  v-if="attachment.maxSizeError && attachment.formatError && attachment.resolutionError">
              Prekročená povolená veľkosť fotografie {{ MAX_SIZE_OF_ATTACHMENT | fileSizeFormat }}.
              <br/>Fotografia nie je v podporovanom formáte.
            </span>
            <div v-else>
              <span v-if="attachment.maxSizeError"
                    class="font-orange font-light font-tiny upload-container__file__error">
                Prekročená povolená veľkosť fotografie {{ MAX_SIZE_OF_ATTACHMENT | fileSizeFormat }}.
              </span>
              <span v-if="attachment.formatError"
                    class="font-orange font-light font-tiny upload-container__file__error">
                Fotografia nie je v podporovanom formáte.
              </span>
              <span v-if="attachment.resolutionError"
                    class="font-orange font-light font-tiny upload-container__file__error">
                Fotografia musí mať rozlíšenie minimálne {{ MIN_IMAGE_WIDTH }} x {{ MIN_IMAGE_HEIGHT }} obrazových bodov, nahrajte fotografiu vo vyhovujúcom rozlíšení.
              </span>
            </div>
          </h4>
        </div>
        <!-- Attachment size anc action buttons -->
        <div class="f-center f-column-480 align-items-start-480">
          <strong v-if="attachment.size"
                  class="upload-container__file__size mr-1-768 pl-3">
            {{ attachment.size | fileSizeFormat }}
          </strong>
        </div>
        <em class="c-pointer icon icon--cross-gray" @click="removeFile(attachment)"/>
      </li>
    </ul>

    <!-- Attachment hidden input -->
    <!--:disabled="currentAttachments.length === requiredFile.maximumPictures"-->
    <input
      multiple
      type="file"
      :id="`upload-${requiredFile.type}`"
      :name="`upload-${requiredFile.type}`"
      @change="imgChanged($event)"
      :key="currentAttachments.length"
      class="hide"
    />

    <!-- Upload icon and description -->
    <p class="upload-container__upload-info font-tiny mb-1">
      Vyberte fotografie alebo ich umiestnite do vyznačenej oblasti.
      <br>Maximálna veľkosť súboru je {{ MAX_SIZE_OF_ATTACHMENT | fileSizeFormat }}.
      <br>Podporované formáty fotografií sú: jpg, jpeg, png.
    </p>

    <div class="upload-icon">
      <em class="icon icon--upload-red"></em>
    </div>

    <!--v-bind:class="{'upload-container__upload-disabled':currentAttachments.length === requiredFile.maximumPictures}"-->
    <label class="upload-container__upload-btn c-pointer"
           :for="`upload-${requiredFile.type}`">Upload</label>

    <div class="validation-output">
      <div class="error" id="FE013">{{ errorMsg.FE013 }}</div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import {
  ACCEPT_FILE_FORMATS, MIN_IMAGE_HEIGHT, MIN_IMAGE_WIDTH, MAX_SIZE_OF_ATTACHMENT,
} from '@/config';
import { errorMsg } from '@/enums';

export default {
  name: 'Upload',
  props: {
    requiredFile: {
      required: true,
      type: Object,
    },
  },
  data: () => ({
    errorMsg,
    errors: [],
    inputDirty: false,
    counter: 0,
    hover: false,
    MIN_IMAGE_HEIGHT,
    MIN_IMAGE_WIDTH,
    MAX_SIZE_OF_ATTACHMENT,
  }),
  computed: {
    /* Returns all attachments */
    ...mapGetters({
      attachments: 'getAttachments',
    }),
    currentAttachments() {
      return this.$store.getters.getAttachmentsByKey(this.requiredFile.type);
    },
    /*
      * Computed returns array of accept formats *
      */
    accept() {
      return ACCEPT_FILE_FORMATS;
    },
    acceptFormats() {
      return this.accept.split(',').map((format) => format.trim()).filter((format) => format);
    },
  },
  methods: {
    /*
      * Returns array of object with file and validation
      */
    validateFiles(files) {
      const filesToUpload = [];
      files.map((file) => {
        const { name, size, type } = file;
        const extension = /(?:\.([^.]+))?$/.exec(name)[1];
        const fileToUpload = {
          file,
          maxSizeError: false,
          formatError: false,
          resolutionError: false,
          hash: null,
          type: this.requiredFile.type,
          size,
          fileName: name,
        };
        if (this.acceptFormats.every((format) => format !== type && `.${extension}` !== format)) fileToUpload.formatError = true;
        if (size > MAX_SIZE_OF_ATTACHMENT) fileToUpload.maxSizeError = true;
        const img = new Image();

        img.addEventListener('load', (event) => {
          const { width, height } = event.target;
          const horizontalPictureInvalid = width < MIN_IMAGE_WIDTH || height < MIN_IMAGE_HEIGHT;
          const verticalPictureInvalid = width < MIN_IMAGE_HEIGHT || height < MIN_IMAGE_WIDTH;
          if (horizontalPictureInvalid && verticalPictureInvalid) {
            fileToUpload.resolutionError = true;
          }
          this.$store.commit('ADD_ATTACHMENT', fileToUpload);
          this.$store.dispatch('validateFiles', { allFiles: true });
        });
        img.addEventListener('error', () => {
          fileToUpload.formatError = true;
          this.$store.commit('ADD_ATTACHMENT', fileToUpload);
          this.$store.dispatch('validateFiles', { allFiles: true });
        });
        img.src = URL.createObjectURL(file);
        return file;
      });
      return filesToUpload;
    },
    /*
      * Method triggered when files change
      * */
    imgChanged(evt) {
      this.errors = [];
      this.inputDirty = true;
      this.hover = false;
      this.counter = 0;
      const newFiles = evt.target.files || evt.dataTransfer.files;
      if (!newFiles) return;
      if (newFiles.length + this.currentAttachments.length > this.requiredFile.maximumPictures) {
        this.errors.push('FE013');
        return;
      }
      this.validateFiles(Array.from(newFiles));
    },
    /*
      * Method remove file from value prop
      * */
    removeFile(fileToUpload) {
      this.$store.commit('REMOVE_ATTACHMENT', fileToUpload);
      if (this.currentAttachments.length < this.requiredFile.maximumPictures) {
        this.errors.splice(this.errors.indexOf('FE013'), 1);
      }
      this.$store.dispatch('validateFiles');
    },
    /*
      * On drag enter show hover effect
      * */
    dragEnter() {
      this.counter += 1;
      this.hover = true;
    },
    /*
      * On drag enter hide hover effect
      * */
    dragLeave() {
      this.counter -= 1;
      if (this.counter === 0) this.hover = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~union_styles/scss/variables";
@import "~union_styles/scss/helpers/mixins/flexbox";
@import "~union_styles/scss/helpers/mixins/position";
@import "~union_styles/scss/helpers/mixins/dimension";

.upload-icon {
  position: absolute;
  top: 0;
  right: 0;
  @include same-width-height(50px);
  transform: translate(50%, -50%);
  border-radius: 50%;
  border: 1px solid $border-color-primary;
  background-color: $color-light;
  @include f-center();

  i {
    @include same-width-height(35px);
  }

  @media (max-width: 768px) {
    display: none;
  }
}

.upload-container {
  $self: &;

  padding: 16px 20px;
  align-items: start;
  justify-content: start;
  flex-direction: column;
  border-radius: 10px;
  border: 1px dashed $border-color-primary;
  transition: all .4s ease;

  .icon {
    &--attachment-green, &--attachment-orange {
      min-width: 47.7px;
    }
  }

  &__upload-btn {
    display: block;
    width: fit-content;
    padding: 12px 48px;
    border-radius: 8px;
    background-color: $color-primary;
    color: $color-light;
  }

  &__upload-disabled {
    background-color: $color-gray;
  }

  &__upload-info {
    max-width: 350px;
  }

  &__file-group {
    list-style-type: none;
    width: 100%;
    margin-bottom: 30px;
  }

  .icon.icon--cross-gray {
    position: absolute;
    right: 35px;
  }

  .upload-container__file__size {
    margin-right: 45px !important;
  }

  &__file {
    $file_self: &;
    @include f-vertical-center();
    background-color: $color-light;
    justify-content: space-between;
    padding: 18px 36px;
    width: 100%;
    border-radius: 8px;
    display: flex;
    margin-bottom: 20px;
    border: 1px solid $border-color-primary;

    &:last-child {
      margin-bottom: 0;
    }

    &__size {
      white-space: nowrap;
    }

    &__error {
      margin-top: 3px;
      display: none;
    }

    &__upload-remove {
      display: none;
      border: 1px solid $border-color-primary;
      padding: 4px 12px;
      font-size: 13px;
      min-height: unset;
      border-radius: 6px;
      margin-left: 46.6px;
      margin-top: 10px;
      @media (max-width: 480px) {
        display: block;
      }
    }

    &--invalid {
      border-color: $color-warning;

      #{$file_self}__size {
        color: $color-warning;
      }

      #{$file_self}__error {
        display: block;
      }
    }
  }

  &--has-file {
    padding: 40px 30px;

    #{$self}__upload-btn {
      align-self: flex-start;
    }

    #{$self}__upload-info {
      text-align: left;
      align-self: flex-start;
    }
  }

  &--hover {
    border-style: solid;
    border-color: $color-primary;
    background-color: rgba($color-primary, 0.02);
  }

  &--invalid {
    .snack-bar.orange {
      display: flex !important;
    }

    border-color: $color-warning !important;
    border-style: solid;
  }

  @media (max-width: 768px) {
    padding: 0;
    border: none !important;

    &__upload-btn {
      align-self: flex-start;
    }
    &__upload-info {
      align-self: flex-start;
      text-align: left
    }

    &__file {
      padding: 12px 22px;
    }

    .icon {
      &--attachment-green, &--attachment-orange {
        height: 40px;
        width: 36.6px;
        min-width: 36.6px;
      }
    }

    .upload-container__file__size {
      margin-right: 60px !important;
    }
  }

  @media (max-width: 480px) {
    &__file {
      align-items: flex-start;
      flex-direction: column;

      &__size {
        padding-top: 5px;
        padding-left: 46.6px;
      }

      .icon.icon--cross-gray {
        position: absolute;
        right: 10px;
      }

      .file-name {
        display: block;
        max-width: 90%;
      }

    }
  }

  .validation-output {
    margin-top: 1rem;
    padding: 0;

    div.error {
      color: hsl(28, 100%, 66%);
    }
  }

}
</style>
